import { FirebaseClientConfigSchema, parseEnv } from '@kilat/shared';
import { z } from 'zod';

/**
 * Validated env for apps/dashboard.
 * Reads import.meta.env at boot; throws SchemaValidationFailedError if invalid.
 */

const RuntimeEnvSchema = z.object({
  MODE: z.enum(['development', 'production', 'test']),
  VITE_FIREBASE_API_KEY: z.string().min(1),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().min(1),
  VITE_FIREBASE_PROJECT_ID: z.string().min(1),
  VITE_FIREBASE_STORAGE_BUCKET: z.string().min(1),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().min(1),
  VITE_FIREBASE_APP_ID: z.string().min(1),
  VITE_FIREBASE_MEASUREMENT_ID: z.string().optional(),
  VITE_USE_EMULATORS: z
    .union([z.literal('true'), z.literal('false')])
    .default('false')
    .transform((v) => v === 'true'),
  VITE_RECAPTCHA_SITE_KEY: z
    .string()
    .optional()
    .describe('reCAPTCHA Enterprise site key for App Check (prod)'),
  /**
   * Web Push for operators (bookings, payments, billing, the daily digest):
   * getToken() needs it, see lib/push/client.ts. The Platform Setup Wizard also
   * reads it (G3) — a key it cannot observe is one it cannot warn about.
   */
  VITE_FCM_VAPID_KEY: z
    .string()
    .optional()
    .describe('FCM Web Push VAPID key — operator push + surfaced for the setup wizard'),
  VITE_MARKETING_ORIGIN: z
    .string()
    .default('https://kilatcarwash.net')
    .describe(
      'Marketing site origin — where the login screen sends brand-new operators to sign up',
    ),
});

const raw = parseEnv(RuntimeEnvSchema, import.meta.env, 'dashboard');

export const env = {
  mode: raw.MODE,
  useEmulators: raw.VITE_USE_EMULATORS,
  recaptchaSiteKey: raw.VITE_RECAPTCHA_SITE_KEY,
  fcmVapidKey: raw.VITE_FCM_VAPID_KEY,
  marketingOrigin: raw.VITE_MARKETING_ORIGIN,
  firebase: parseEnv(
    FirebaseClientConfigSchema,
    {
      apiKey: raw.VITE_FIREBASE_API_KEY,
      authDomain: raw.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: raw.VITE_FIREBASE_PROJECT_ID,
      storageBucket: raw.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: raw.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: raw.VITE_FIREBASE_APP_ID,
      measurementId: raw.VITE_FIREBASE_MEASUREMENT_ID,
    },
    'dashboard.firebase',
  ),
} as const;
export type Env = typeof env;
